import React, { useState } from 'react';
import { X, ChevronDown } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import { updateIntake, client } from '../services/api';
import { useToast } from '../hooks/useToastContext';
import { IntakeUserAssignmentDialog } from './IntakeUserAssignmentDialog';
import { IntakeRecord } from '@/store/slices/intakeSlice';

interface IntakeSelectionActionsProps {
  selectedIntakes: IntakeRecord[];
  onClearSelection: () => void;
  onAssignComplete?: () => void;
}

const IntakeSelectionActions: React.FC<IntakeSelectionActionsProps> = ({
  selectedIntakes,
  onClearSelection,
  onAssignComplete,
}) => {
  const selectedIntakeIds = selectedIntakes.map(intake => intake.id);
  const hasAnyAssigned = selectedIntakes.some(
    intake => intake.assigned_to != null && intake.assigned_to !== ''
  );
  const { error: showError } = useToast();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);

  const handleAssignClick = () => {
    // Delay opening the dialog slightly to allow the dropdown menu to close
    // and cleanup its focus/pointer-events state. This prevents the UI from
    // becoming unresponsive if the user cancels the dialog.
    setTimeout(() => {
      setAssignDialogOpen(true);
    }, 100);
  };

  const handleUnassignClick = async () => {
    setIsUnassigning(true);
    try {
      // Update each selected intake to remove assignment
      await Promise.all(
        selectedIntakeIds.map(intakeId =>
          updateIntake({
            path: { intake_id: intakeId },
            body: { assigned_to: null },
            client,
          })
        )
      );
      onClearSelection();
      onAssignComplete?.();
    } catch {
      showError(
        'Failed to unassign intakes',
        'Please try again or refresh the page.'
      );
    } finally {
      setIsUnassigning(false);
    }
  };

  return (
    <>
      <div className='fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50'>
        <div className='bg-primary text-primary-foreground px-2 py-2 rounded-lg shadow-lg flex items-center gap-2'>
          <div className='flex items-center gap-2 h-7 px-2 bg-black/20 rounded border border-white/10'>
            <span className='text-sm'>{selectedIntakeIds.length} selected</span>
            <Button
              onClick={onClearSelection}
              size='icon'
              variant='ghost'
              className='hover:bg-black/20'
              aria-label='Clear selection'
            >
              <X className='w-3.5 h-3.5' />
            </Button>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                className='h-7 px-2 text-sm bg-black/20 border border-white/10 rounded hover:bg-black/30 hover:text-primary-foreground'
              >
                Actions
                <ChevronDown className='w-3.5 h-3.5' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onClick={handleAssignClick}>
                Assign
              </DropdownMenuItem>
              {hasAnyAssigned && (
                <DropdownMenuItem
                  onClick={handleUnassignClick}
                  disabled={isUnassigning}
                >
                  {isUnassigning ? 'Unassigning...' : 'Unassign'}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <IntakeUserAssignmentDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        intakes={selectedIntakes}
        onSuccess={() => {
          onClearSelection();
          onAssignComplete?.();
        }}
      />
    </>
  );
};

export default IntakeSelectionActions;
